import * as Notifications from 'expo-notifications';
import Constants from 'expo-constants';
import { getInstallationID } from '@/utils/installation-id';
import { gql, useMutation } from '@apollo/client';
import {
  NotificationToken_SaveMutation,
  NotificationToken_SaveMutationVariables,
} from '@/types';
import { partnerSupplier } from '@/utils/environment';
import { Platform } from 'react-native';
import { useCallback, useMemo } from 'react';

export const SAVE_TOKEN_MUTATION = gql`
  mutation NotificationToken_Save(
    $deviceIdentifier: String!
    $notificationToken: String!
    $businessOrganizationUUID: uuid
  ) {
    insert_notification_tokens_one(
      object: {
        device_identifier: $deviceIdentifier
        notification_token: $notificationToken
        business_organization_uuid: $businessOrganizationUUID
      }
      on_conflict: {
        constraint: backend_notificationtoken_bizorg_employee_device_identifier_is_
        update_columns: [notification_token]
      }
    ) {
      uuid
    }
  }
`;

export const useSaveNotificationToken = () => {
  const [saveToken, { loading: isSaving }] = useMutation<
    NotificationToken_SaveMutation,
    NotificationToken_SaveMutationVariables
  >(SAVE_TOKEN_MUTATION);

  const checkAndSaveToken = useCallback(
    async (result?: Notifications.NotificationPermissionsStatus) => {
      if (Platform.OS === 'web') {
        return;
      }
      /*
       * Saving notification tokens in development mode can cause errors if
       * the simulator or development build is connected to the production backend.
       * Only remove this check when specifically testing notifications in development.
       */
      if (__DEV__ && process.env.NODE_ENV !== 'test') {
        console.warn(
          'WARNING: preventing attempt to save notification token in dev mode.',
        );
        return;
      }
      result = result || (await Notifications.getPermissionsAsync());
      if (result.status === 'granted') {
        // Android channel must exist before requesting a token
        if (Platform.OS === 'android') {
          await Notifications.setNotificationChannelAsync('default', {
            importance: Notifications.AndroidImportance.MAX,
            name: 'Default',
          });
        }

        const { data: notificationToken } =
          await Notifications.getExpoPushTokenAsync({
            projectId: Constants.expoConfig?.extra?.eas?.projectId,
          });
        await saveToken({
          variables: {
            businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
            deviceIdentifier: await getInstallationID(),
            notificationToken,
          },
        });
      }
    },
    [saveToken],
  );

  return useMemo(
    () => ({
      checkAndSaveToken,
      isSaving,
    }),
    [checkAndSaveToken, isSaving],
  );
};
